import { Alert, Button, Drawer, Empty, Input, Segmented, Select, Switch, Tag, message as antdMessage } from "antd";
import { SendOutlined } from "@ant-design/icons";
import { useEffect, useMemo, useState } from "react";
import { createCampaign } from "../api/campaigns";
import type { GroupMember } from "../api/groups";
import {
  getEmailTemplate,
  listEmailTemplates,
} from "../api/emailTemplates";
import type { EmailTemplateSummary } from "../api/emailTemplates";

type Props = {
  open: boolean;
  onClose: () => void;
  groupId: string;
  members: GroupMember[];
  onSent?: (campaignId: string) => void;
};

const VAR_TOKENS = ["{{Name}}", "{{Company}}", "{{Email}}"];

function renderPreview(template: string, vars: Record<string, string>): string {
  return template.replace(/\{\{\s*([A-Za-z_][\w.]*)\s*\}\}/g, (_, k: string) =>
    String(vars[k] ?? `{{${k}}}`),
  );
}

export function BulkComposeDrawer({ open, onClose, groupId, members, onSent }: Props) {
  const [mode, setMode] = useState<"text" | "html">("text");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [personalize, setPersonalize] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [templates, setTemplates] = useState<EmailTemplateSummary[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [htmlBody, setHtmlBody] = useState("");
  const [loadingHtml, setLoadingHtml] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode("text");
    setSubject("");
    setBody("");
    setHtmlBody("");
    setSelectedTemplateId(null);
    setPersonalize(true);
    setPreviewIndex(0);
    listEmailTemplates()
      .then((d) => setTemplates(d.templates))
      .catch(() => setTemplates([]));
  }, [open]);

  useEffect(() => {
    if (mode !== "html" || !selectedTemplateId) {
      setHtmlBody("");
      return;
    }
    setLoadingHtml(true);
    getEmailTemplate(selectedTemplateId)
      .then((d) => setHtmlBody(d.template.html))
      .catch(() => {
        antdMessage.error("加载模板失败");
        setHtmlBody("");
      })
      .finally(() => setLoadingHtml(false));
  }, [mode, selectedTemplateId]);

  const sample = members[previewIndex] ?? null;

  const previewVars = useMemo<Record<string, string>>(() => {
    if (!sample) return {};
    const v = (sample.vars as Record<string, string>) ?? {};
    return {
      Name: typeof v.Name === "string" && v.Name ? v.Name : sample.name ?? "",
      Company: typeof v.Company === "string" ? v.Company : "",
      Email: sample.email,
      ...v,
    };
  }, [sample]);

  const insert = (token: string) => {
    setBody((b) => `${b}${b.endsWith(" ") || !b ? "" : " "}${token}`);
  };

  const finalBody = mode === "html" ? htmlBody : body;

  const onSubmit = async () => {
    if (!subject.trim()) {
      antdMessage.warning("主题不能为空");
      return;
    }
    if (mode === "html") {
      if (!selectedTemplateId || !htmlBody.trim()) {
        antdMessage.warning("请选择一个 HTML 模板");
        return;
      }
    } else if (!body.trim()) {
      antdMessage.warning("正文不能为空");
      return;
    }
    if (members.length === 0) {
      antdMessage.warning("群组里还没有成员");
      return;
    }
    setSubmitting(true);
    try {
      const { campaignId } = await createCampaign({
        groupId,
        subject: subject.trim(),
        bodyTemplate: finalBody,
        isHtml: mode === "html",
        personalize,
      });
      antdMessage.success(`已加入发送队列(${members.length} 人)`);
      onSent?.(campaignId);
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "send_failed";
      antdMessage.error(`创建失败: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      title="新建群发"
      open={open}
      onClose={onClose}
      width={Math.min(720, window.innerWidth)}
      placement="right"
      maskClosable={false}
      keyboard={false}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 15, color: "var(--ant-color-text-secondary)" }}>
            将发送给群组中 {members.length} 位成员
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <Button onClick={onClose}>取消</Button>
            <Button type="primary" icon={<SendOutlined />} loading={submitting} onClick={onSubmit}>
              发送
            </Button>
          </div>
        </div>
      }
    >
      <div style={{ display: "grid", gap: 16 }}>
        <Segmented
          value={mode}
          onChange={(v) => setMode(v as "text" | "html")}
          options={[
            { label: "文本", value: "text" },
            { label: "HTML 模板", value: "html" },
          ]}
          block
        />

        <div>
          <Label>主题</Label>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={
              mode === "html"
                ? "邮件主题(支持变量,如 Hi {{Name}})"
                : "可使用变量,例如:Hi {{Name}}, 来自 Priomail 的更新"
            }
          />
        </div>

        {mode === "html" ? (
          <div>
            <Label>选择 HTML 模板</Label>
            {templates.length === 0 ? (
              <Empty
                description={
                  <span>
                    还没有 HTML 模板。先去{" "}
                    <a href="/settings" target="_blank" rel="noreferrer">
                      设置 → 邮件模板
                    </a>{" "}
                    上传
                  </span>
                }
                style={{ padding: 24 }}
              />
            ) : (
              <Select
                value={selectedTemplateId ?? undefined}
                onChange={(v) => setSelectedTemplateId(v)}
                placeholder="选择一个模板"
                style={{ width: "100%" }}
                options={templates.map((t) => ({ value: t.id, label: t.name }))}
              />
            )}
            {selectedTemplateId && htmlBody ? (
              <div style={{ marginTop: 12 }}>
                <Label>模板预览</Label>
                <iframe
                  srcDoc={htmlBody}
                  title="模板预览"
                  sandbox=""
                  style={{
                    width: "100%",
                    height: 380,
                    border: "1px solid var(--ant-color-border)",
                    borderRadius: 8,
                    background: "white",
                  }}
                />
              </div>
            ) : loadingHtml ? (
              <div style={{ marginTop: 8, fontSize: 14, color: "var(--ant-color-text-secondary)" }}>
                加载中…
              </div>
            ) : null}
          </div>
        ) : (
          <div>
            <Label>正文模板</Label>
            <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
              {VAR_TOKENS.map((t) => (
                <Tag
                  key={t}
                  color="blue"
                  style={{ cursor: "pointer", marginInlineEnd: 0 }}
                  onClick={() => insert(t)}
                >
                  {t}
                </Tag>
              ))}
              <span style={{ fontSize: 14, color: "var(--ant-color-text-secondary)" }}>
                点变量插入
              </span>
            </div>
            <Input.TextArea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              autoSize={{ minRows: 8, maxRows: 18 }}
              placeholder="你好 {{Name}},想跟你同步一下…"
              style={{ fontSize: 16, lineHeight: 1.6 }}
            />
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Switch checked={personalize} onChange={setPersonalize} />
          <span style={{ fontSize: 15 }}>启用变量替换(逐人渲染)</span>
        </div>

        {mode === "text" && personalize && sample ? (
          <div
            style={{
              background: "var(--ant-color-bg-layout)",
              border: "1px solid var(--ant-color-border)",
              borderRadius: 12,
              padding: 12,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8,
                gap: 8,
              }}
            >
              <Tag color="purple" style={{ marginInlineEnd: 0 }}>
                预览
              </Tag>
              <span style={{ fontSize: 14, color: "var(--ant-color-text-secondary)" }}>
                {sample.email}
              </span>
              <div style={{ display: "flex", gap: 4 }}>
                <Button
                  size="small"
                  disabled={previewIndex === 0}
                  onClick={() => setPreviewIndex((i) => Math.max(0, i - 1))}
                >
                  ‹
                </Button>
                <Button
                  size="small"
                  disabled={previewIndex >= members.length - 1}
                  onClick={() =>
                    setPreviewIndex((i) => Math.min(members.length - 1, i + 1))
                  }
                >
                  ›
                </Button>
              </div>
            </div>
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 6 }}>
              {renderPreview(subject, previewVars) || "(主题预览)"}
            </div>
            <div
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontSize: 15,
                lineHeight: 1.6,
                color: "var(--ant-color-text)",
              }}
            >
              {renderPreview(body, previewVars) || "(正文预览)"}
            </div>
          </div>
        ) : null}

        {members.length === 0 ? (
          <Alert type="warning" showIcon message="群组里还没有成员,先去添加" />
        ) : null}
      </div>
    </Drawer>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 14,
        color: "var(--ant-color-text-secondary)",
        marginBottom: 4,
        fontWeight: 500,
      }}
    >
      {children}
    </div>
  );
}
